import { matchPath, type Params } from 'react-router';
import type { Entrypoint, PageType } from 'types';
import * as uuid from 'uuid';

import { log } from '~/utils/log';
import { deriveSourceMedium } from '~/utils/sourceMedium';

export const SEARCH_ENGINES = [
  'baidu',
  'bing',
  'duckduckgo',
  'ecosia',
  'google',
  'qwant',
  'yahoo',
  'yandex'
];

const STORAGE_KEY = 'pageContext';

export const PLP_SEGMENTS = ['release-dates', 'sole-market', 'clothing'];

const SOCIAL_MEDIUMS = ['social', 'social-paid', 'social-organic'];

const SEARCH_MEDIUMS = ['cpc', 'organic', 'paid-search', 'ppc'];

const SOCIAL_SOURCES = [
  'facebook',
  'instagram',
  'linkedin',
  'pinterest',
  'reddit',
  'snapchat',
  'tiktok',
  'twitter',
  'youtube'
];

type StoredState = {
  currentContext: unknown;
  entry_point?: Entrypoint;
  pageSeq?: number;
  pageType: PageType;
  pageViewId?: string;
  path?: string;
  previousContext: unknown;
  previousPagePath?: string;
  previousPagePosition?: string;
  previousPageType?: PageType;
  previousPageViewId?: string;
  source_medium?: string;
};

const DEFAULT_STATE: StoredState = {
  currentContext: null,
  entry_point: undefined,
  pageSeq: 0,
  pageType: 'unknown',
  pageViewId: undefined,
  path: undefined,
  previousContext: null,
  previousPagePath: undefined,
  previousPagePosition: undefined,
  previousPageType: undefined,
  previousPageViewId: undefined,
  source_medium: undefined
};

function load(): StoredState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    // sessionStorage may be unavailable
  }

  return { ...DEFAULT_STATE };
}

function save(state: StoredState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable
  }
}

function isSearchEngineReferrer(referrer: string): boolean {
  try {
    const hostname = new URL(referrer).hostname.toLowerCase();

    return SEARCH_ENGINES.some((engine) => hostname.includes(engine));
  } catch {
    return false;
  }
}

function deriveEntrypoint(current: Entrypoint | undefined): Entrypoint {
  if (current) return current;

  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source')?.toLowerCase() ?? '';
  const medium = params.get('utm_medium')?.toLowerCase() ?? '';

  if (medium === 'email') return 'email';

  if (SOCIAL_SOURCES.includes(source) || SOCIAL_MEDIUMS.includes(medium)) {
    return 'social';
  }

  if (SEARCH_MEDIUMS.includes(medium)) return 'search';

  if (isSearchEngineReferrer(document.referrer)) return 'search';

  return 'unknown';
}

const MARKETPLACE_SELLER_LISTINGS_TYPES: Partial<Record<string, PageType>> = {
  active: 'marketplace_seller_active',
  dashboard: 'marketplace_seller_dashboard',
  inactive: 'marketplace_seller_inactive',
  sold: 'marketplace_seller_sold'
};

type PageTypeRoute = {
  pageType: PageType | ((params: Params, query: URLSearchParams) => PageType);
  path: string;
};

const PAGE_TYPE_ROUTES: PageTypeRoute[] = [
  { pageType: 'homepage', path: '/' },
  { pageType: 'article_feed', path: '/news' },
  { pageType: 'article_feed', path: '/news/footwear' },
  { pageType: 'article_feed', path: '/news/size-guides' },
  { pageType: 'article_feed', path: '/news/lifestyle' },
  { pageType: 'article_feed', path: '/news/clothing' },
  { pageType: 'article_feed', path: '/news/features' },
  { pageType: 'article', path: '/news/*' },
  { pageType: 'article', path: '/features/*' },
  { pageType: 'sale', path: '/sale/*' },
  { pageType: 'pdp', path: '/s/*' },
  { pageType: 'profile', path: '/profile' },
  { pageType: 'profile_details', path: '/profile/my-account' },
  { pageType: 'profile_favourite_brands', path: '/profile/favourite-brands' },
  { pageType: 'recently_viewed', path: '/profile/recently-viewed' },
  { pageType: 'profile_size', path: '/profile/shoe-size' },
  { pageType: 'orders', path: '/profile/orders/:id?' },
  { pageType: 'profile_contact_pref', path: '/profile/contact-preferences' },
  { pageType: 'notifications', path: '/profile/notifications' },
  { pageType: 'profile_password_reset', path: '/change-password' },
  { pageType: 'profile_password_reset', path: '/reset-password' },
  {
    pageType: 'marketplace_seller_register',
    path: '/marketplace/sell/register'
  },
  {
    pageType: 'marketplace_seller_register',
    path: '/marketplace/sell/register/success'
  },
  {
    pageType: 'marketplace_seller_register',
    path: '/marketplace/sell/register/complete'
  },
  {
    pageType: 'marketplace_seller_verification',
    path: '/marketplace/sell/register/verification'
  },
  {
    pageType: 'marketplace_seller_new_listing',
    path: '/marketplace/sell/listing/new/bulk'
  },
  {
    pageType: 'marketplace_seller_edit_listing',
    path: '/marketplace/sell/listing/:listingId/edit'
  },
  {
    pageType: 'marketplace_seller_image_upload',
    path: '/marketplace/sell/order/:orderId/images'
  },
  {
    pageType: (params) =>
      MARKETPLACE_SELLER_LISTINGS_TYPES[params.viewType ?? ''] ?? 'unknown',
    path: '/marketplace/sell/listings/:viewType'
  },
  ...PLP_SEGMENTS.flatMap((segment): PageTypeRoute[] => [
    { pageType: 'pdp', path: `/:gender?/${segment}/:brand/:subBrand/:slug/*` },
    {
      pageType: (_params, query) => (query.has('q') ? 'search' : 'plp'),
      path: `/:gender?/${segment}/:brand?/:subBrand?`
    }
  ])
];

export function derivePageTypeFromUrl(
  url: string | undefined
): PageType | undefined {
  if (!url) return;

  const [path = '', query] = url.split('?');
  const pathname = path.startsWith('/') ? path : `/${path}`;

  for (const route of PAGE_TYPE_ROUTES) {
    const match = matchPath(route.path, pathname);

    if (!match) continue;

    return typeof route.pageType === 'function'
      ? route.pageType(match.params, new URLSearchParams(query ?? ''))
      : route.pageType;
  }

  return 'unknown';
}

export function setNavigationContext<T = unknown>(context: T) {
  const state = load();

  state.currentContext = context;
  save(state);
}

export function getCurrentContext<T = unknown>(): T | null {
  return (load().currentContext as T) ?? null;
}

export function getPreviousContext<P = unknown>(): P | null {
  return (load().previousContext as P) ?? null;
}

export function getEntrypoint(): Entrypoint {
  return load().entry_point ?? 'unknown';
}

export function getPageType(): PageType {
  return load().pageType ?? 'unknown';
}

export function getPageSeq(): number {
  return load().pageSeq ?? 0;
}

export function getPageViewId(): string | undefined {
  return load().pageViewId;
}

export function getPreviousPagePath(): string | undefined {
  return load().previousPagePath;
}

export function getSourceAndMedium(): string | undefined {
  return load().source_medium;
}

export function getPreviousPageType(): PageType | undefined {
  const state = load();
  const ctx = state.previousContext as { previousPageType?: PageType } | null;

  return ctx?.previousPageType ?? state.previousPageType;
}

export function getPreviousPagePosition(): string | undefined {
  const ctx = getPreviousContext<{ pagePosition?: string }>();

  return ctx?.pagePosition;
}

export function getPreviousPageViewId(): string | undefined {
  return load().previousPageViewId;
}

let advancedSinceLoad = false;

export function advanceNavigationContext(path: string) {
  if (typeof document === 'undefined') return;

  const prevState = load();
  const sameUrlRerender = advancedSinceLoad && path === prevState.path;

  advancedSinceLoad = true;

  if (sameUrlRerender) {
    log.info('advanceNavigationContext', load());

    return;
  }

  const nextState: StoredState = {
    ...prevState,
    currentContext: null,
    entry_point: deriveEntrypoint(prevState.entry_point),
    pageSeq: (prevState.pageSeq ?? 0) + 1,
    pageType: derivePageTypeFromUrl(path) ?? 'unknown',
    pageViewId: uuid.v4(),
    path,
    previousContext: prevState.currentContext,
    previousPagePath: prevState.path?.split(/[?#]/)[0],
    previousPagePosition: getPreviousPagePosition(),
    previousPageType: derivePageTypeFromUrl(prevState.path),
    previousPageViewId: prevState.pageViewId,
    source_medium:
      prevState.source_medium ||
      deriveSourceMedium(
        window.location.search,
        document.referrer,
        window.location.hostname
      )
  };

  save(nextState);
  log.info('Current Navigation Context:', nextState);
}
